import { DATA_SOURCE_IDS, FUSION_TYPES } from '@discover-frontend/constants';
import { getProductScope } from '@discover-frontend/utils';

import { clearQuery, preprocessStanleyTargetQueryForApply } from 'src/operations/query';
import {
  hasGwiFusion,
  hasMriSimmonsFusion,
  hasTgiFusion,
  hasWWGwiMediaFusion,
} from 'src/utils/AuthHelperMethods';

import { StanleyQuerySuggestion } from '../../types';

export interface StanleyMembershipTargetContext {
  query_type: string;
  target_label?: string;
  audience_timeframe?: string;
  results_timeframe?: number | string;
  base_query?: {
    whole_population?: boolean;
    query_block?: unknown;
  };
  target_membership_id?: string | null;
  data_source?: string;
}

/**
 * Builds the POST /membership/size payload for a Stanley suggestion, mirroring
 * {@link apps/hx-workspace/src/sagas/MembershipSagas.js} but using the suggestion's target_query.
 */
export const buildStanleyMembershipBody = (
  dataSource: string,
  target: StanleyMembershipTargetContext,
  suggestion: StanleyQuerySuggestion
): Record<string, unknown> | null => {
  const rawTargetQuery = suggestion.query_json?.target_query;
  if (!rawTargetQuery) {
    return null;
  }

  const prepared = preprocessStanleyTargetQueryForApply(rawTargetQuery);
  if (!prepared) {
    return null;
  }

  let audience: Record<string, unknown> = {
    query_type: target.query_type,
    target_query: prepared,
    base_query:
      target.base_query && target.base_query.query_block
        ? {
            whole_population: false,
            query_block: clearQuery(target.base_query.query_block as never),
          }
        : { whole_population: true, query_block: null },
    target_membership_id: target.target_membership_id,
  };

  if (audience.query_type !== 'interest') {
    audience = {
      ...audience,
      audience_timeframe: target.audience_timeframe,
      results_timeframe: target.results_timeframe ?? 52,
    };
  }

  const data: Record<string, unknown> = {
    ...audience,
    scope: getProductScope(),
    source: target.data_source || dataSource,
  };

  if (data.source === DATA_SOURCE_IDS.TWITTER) {
    if (hasMriSimmonsFusion()) data.partner_source = FUSION_TYPES.MRI_SIMMONS;
    if (hasGwiFusion()) data.partner_source = FUSION_TYPES.GWI;
    if (hasWWGwiMediaFusion()) data.partner_source = FUSION_TYPES.WW_GWI_MED;
    if (hasTgiFusion()) data.partner_source = FUSION_TYPES.TGI;
  }

  return data;
};
